/**
 * Shared in-memory toast state and subscriptions. Keeps one visible toast; dismissal closes it and
 * schedules later removal, while the React hook subscribes to updates.
 *
 * @module apps/web/src/hooks/use-toast
 */

import { useEffect, useState } from 'react';
import type { ToastActionElement, ToastProps } from '@/components/ui/toast';

/** Maximum visible toast count retained when a new notification is added. */
const TOAST_LIMIT = 1;
/** Delay between dismissing a toast and removing its record from shared memory. */
const TOAST_REMOVE_DELAY = 1000000;

/** Toast display props plus the local identity used for later update/dismiss operations. */
type ToasterToast = ToastProps & {
  /** Process-local toast identifier used to update, dismiss, or remove it. */
  id: string;
  /** Optional React content shown as the toast heading. */
  title?: React.ReactNode;
  /** Optional React content explaining the notification. */
  description?: React.ReactNode;
  /** Optional action element rendered alongside the toast message. */
  action?: ToastActionElement;
};


/** Process-local sequence used to allocate toast identities. */
let count = 0;

/** Allocate a process-local string ID, wrapping the counter before exceeding JavaScript’s safe integer range. */
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

/** Literal toast operation names, the discriminants understood by the shared toast reducer. */
type ActionType = {
  /** Discriminator for inserting a new toast subject to the visible limit. */
  ADD_TOAST: 'ADD_TOAST';
  /** Discriminator for merging changes into an existing toast by ID. */
  UPDATE_TOAST: 'UPDATE_TOAST';
  /** Discriminator for closing one or all toasts and scheduling removal. */
  DISMISS_TOAST: 'DISMISS_TOAST';
  /** Discriminator for deleting one or all toast records from memory. */
  REMOVE_TOAST: 'REMOVE_TOAST';
};

/** Reducer messages for adding, updating, dismissing, or removing one or all toast records. */
type Action =
  | {
      /** Reducer discriminator selecting add, update, dismiss, or remove behavior. */
      type: ActionType['ADD_TOAST'];
      /** Full toast for addition or partial toast carrying the target ID for update. */
      toast: ToasterToast;
    }
  | {
      /** Reducer discriminator selecting add, update, dismiss, or remove behavior. */
      type: ActionType['UPDATE_TOAST'];
      /** Full toast for addition or partial toast carrying the target ID for update. */
      toast: Partial<ToasterToast>;
    }
  | {
      /** Reducer discriminator selecting add, update, dismiss, or remove behavior. */
      type: ActionType['DISMISS_TOAST'];
      /** Target toast identifier; omission applies dismissal or removal to all records. */
      toastId?: ToasterToast['id'];
    }
  | {
      /** Reducer discriminator selecting add, update, dismiss, or remove behavior. */
      type: ActionType['REMOVE_TOAST'];
      /** Target toast identifier; omission applies dismissal or removal to all records. */
      toastId?: ToasterToast['id'];
    };

/** Current ordered notification collection shared by mounted toast subscribers. */
interface State {
  /** Ordered in-memory toast records retained by the shared reducer. */
  toasts: ToasterToast[];
}

/** Removal timer registry keyed by toast ID, preventing duplicate timers for repeated dismissals. */
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

/** Schedule one delayed REMOVE_TOAST action per ID. Repeated dismissals reuse the existing timer rather than prolonging its lifetime. */
const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

/**
 * Apply add/update/dismiss/remove actions to toast state. Dismissal also queues removal, so this
 * reducer has a timer side effect.
 */
export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      };

    case 'DISMISS_TOAST': {
      const { toastId } = action;

      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

/** Mounted toast-state subscribers notified after each reducer dispatch. */
const listeners: Array<(state: State) => void> = [];

/** Current process-local toast state retained across individual hook mounts. */
let memoryState: State = { toasts: [] };

/** Apply one toast reducer action to shared memory and synchronously notify every mounted subscriber. */
function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

/** Caller-supplied notification content before the helper assigns its local ID. */
type Toast = Omit<ToasterToast, 'id'>;

/**
 * Publish a toast to the shared single-toast store and return its ID plus update/dismiss functions.
 */
function toast({ ...props }: Toast) {
  const id = genId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  return {
    id: id,
    dismiss,
    update,
  };
}

/**
 * Subscribe a React component to shared toast state and expose toast creation and dismissal
 * actions.
 */
function useToast() {
  const [state, setState] = useState<State>(memoryState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
  };
}

export { useToast, toast };
